'use client';

import classNames from 'classnames';
import Image from 'next/image';
import { useParams, usePathname, useRouter } from 'next/navigation';
import React, { FC, useCallback, useEffect, useState } from 'react';

import { Route } from '@/app/[lang]/Router/createRoute';
import {
  AVI_ROUTE,
  HOME_ROUTE,
  BLOG_ROUTE,
  POLY_ROUTE,
  GAMES_ROUTE,
  PRODUCTS_ROUTE,
  ABOUT_US_ROUTE,
  LANDBASED_ROUTE,
  CONTACT_US_ROUTE,
} from '@/app/[lang]/Router/routes';
import Link from '@/components/core/Link';
import TabNavigation, { Tab } from '@/components/TabNavigation';
import { isActiveRoute } from '@/helpers';
import { useDeviceSize } from '@/hooks/useDeviceSize';
import { _t, Dict, Lang } from '@/lib/i18n';
import Logo from '@/public/assets/Images/Logo.png';

import HeaderLanguage from './HeaderLanguage';
import MobileHeader from './MobileHeader';

const pages: Route[] = [
  PRODUCTS_ROUTE,
  BLOG_ROUTE,
  ABOUT_US_ROUTE,
  CONTACT_US_ROUTE,
  AVI_ROUTE,
  POLY_ROUTE,
];

interface HeaderProps {
  lang: Lang;
  dict: Dict;
}

// temp
const CUSTOM_ACTIVE_LINKS = ['news', 'blog', 'upcoming-events'];

const Header: FC<HeaderProps> = ({ dict, lang }) => {
  const [isBurgerOpened, setIsBurgerOpened] = useState(false);
  const pathname = usePathname();
  const params = useParams();
  const { isMobile } = useDeviceSize();
  const t = (key: string) => _t(key, dict);
  const router = useRouter();

  const customActiveLinks = useCallback(
    (name: string) => {
      if (name === 'BLOG') {
        return CUSTOM_ACTIVE_LINKS.some((route: string) => pathname.includes(route));
      }
      return false;
    },
    [pathname]
  );

  const pagesWithTabNavbar: { [key: string]: Tab[] } = {
    [HOME_ROUTE.getPath(lang)]: [
      { label: 'LandBased-Section Headers-Menu-Features', sectionID: 'features', active: true },
      { label: 'Header-Menu-Subpage-Events', sectionID: 'upcoming' },
      { label: 'Header-Menu-Subpage-PARTNERS & CERTIFICATIONS', sectionID: 'partners' },
    ],
    [AVI_ROUTE.getPath(lang)]: [
      { label: 'Avi-Section Headers-Menu-Whyavi', sectionID: 'whyAvi' },
      { label: 'Avi-Section Headers-Menu-Prizestorm', sectionID: 'aviPrizeStorm' },
      { label: 'Avi-Section Headers-Menu-Gamedetails', sectionID: 'aviGameDetails' },
      { label: 'Avi-Section Headers-Menu-Gallery', sectionID: 'aviGallery' },
      { label: 'Avi-Section Headers-Menu-News', sectionID: 'aviNews' },
      { label: 'Avi-Section Headers-Menu-FAQ', sectionID: 'aviFAQ' },
    ],
    [POLY_ROUTE.getPath(lang)]: [
      { label: 'Poly-SectionHeaders-Menu-Aboutpoly', sectionID: 'aboutPoly', active: true },
      { label: 'Poly-SectionHeaders-Menu-livecasino', sectionID: 'liveCasino' },
      { label: 'Poly-SectionHeaders-Menu-slots', sectionID: 'slots' },
      { label: 'Poly-SectionHeaders-Menu-news', sectionID: 'news' },
    ],
    [LANDBASED_ROUTE.getPath(lang)]: [
      { label: 'LandBased-Section Headers-Menu-Features', sectionID: 'features', active: true },
      { label: 'LandBased-Section Headers-Menu-Games', sectionID: 'games' },
      { label: 'LandBased-Section Headers-Menu-Cabinets', sectionID: 'cabinets' },
    ],
    [ABOUT_US_ROUTE.getPath(lang)]: [
      { label: 'Aboutus-Section Headers-Menu-Whatwedo', sectionID: 'what-we-do', active: true },
      { label: 'Aboutus-Section Headers-Menu-Licenses and certifications', sectionID: 'licenses' },
      { label: 'Aboutus-Section Headers-Menu-History', sectionID: 'history' },
      { label: 'Aboutus-Section Headers-Menu-Sales team', sectionID: 'team' },
    ],
    [`${GAMES_ROUTE.getPath(lang)}/${params.category}`]: GAMES_ROUTE.childRoutes.map((item) => ({
      type: 'link',
      label: item.name,
      sectionID: item.getPath(params.lang as string),
      active: pathname === item.getPath(params.lang as string),
    })),
    // [NEWS_ROUTE.getPath(lang)]: [
    //   {
    //     label: 'Blog',
    //     sectionID: 'news',
    //     active: true,
    //   },
    //   {
    //     label: 'Upcoming events',
    //     sectionID: 'upcoming',
    //   },
    // ],
  };

  const onClose = () => {
    setIsBurgerOpened(false);
  };

  useEffect(() => {
    if (isMobile && isBurgerOpened) {
      onClose();
    }
  }, []);

  useEffect(() => {
    if (pathname === '/') {
      router.push(`/${lang}`);
    }
  }, []);

  return (
    <>
      <MobileHeader isOppened={isBurgerOpened} pages={pages} onClose={onClose} />
      <div className="header">
        <div className="bigContent">
          <div className="headerContent">
            <div className="headerLogo">
              <Link href={`/${params.lang}`} aria-label="home" prefetch>
                <Image src={Logo} alt="" />
              </Link>
            </div>
            <div className="headerMenu">
              {pages.map((page) => {
                const routeStatus = isActiveRoute(page.getPath(lang) || '', pathname, lang);
                return (
                  <div
                    key={page.path}
                    className={classNames('headerMenuItem', {
                      active:
                        customActiveLinks(page.name) ||
                        (routeStatus?.active && !page.hideParentUrl),
                      clickable: routeStatus?.clickable,
                      poly: page.name === 'Header-Menu-Poly',
                      avi: page.name === 'Header-Menu-Avi',
                    })}
                  >
                    <div className="headerMenuInnerItem">
                      {page.isNotUrl ? (
                        <span className="headerMenuItemTxt">{t(page.name)}</span>
                      ) : (
                        <Link href={page.getPath(lang)} className="headerMenuItemTxt">
                          {t(page.name)}
                        </Link>
                      )}
                      {page.childRoutes.length ? (
                        <>
                          <i className="bc-i-arrow-down" />
                          <div className="headerMenuInnerWrp">
                            <div className="headerMenuInnerHolder">
                              <div className="headerMenuInnerContent">
                                {page.childRoutes.map((childPage) => (
                                  <Link
                                    prefetch
                                    key={childPage.path}
                                    className={classNames('headerMenuInnerRow', {
                                      rico: childPage.name === 'rico-series',
                                    })}
                                    href={childPage.getPath(lang)}
                                  >
                                    <p className="headerMenuInnerTxt">{t(childPage.name)}</p>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
            <HeaderLanguage />
            <div className="burgerMenu" onClick={() => setIsBurgerOpened((prev) => !prev)}>
              <i className="bc-i-burger burgerIcon" />
              <i className="bc-i-close closeIcon" />
            </div>
          </div>
        </div>
      </div>
      {!!pagesWithTabNavbar[pathname] && <TabNavigation tabs={pagesWithTabNavbar[pathname]} />}
    </>
  );
};

export default Header;
